import { apiRequest, apiRequestBlob } from '@/lib/api/client';
import type {
  AiQueryResponse,
  BatchUploadPayload,
  BatchUploadResponse,
  CreateVersionPayload,
  DuplicateCheckItemInput,
  DuplicateCheckResultItem,
  FileAcl,
  FileAiSource,
  FileAuditLog,
  FileDetail,
  FileHubSummary,
  FileLink,
  FilePreview,
  FileListResponse,
  FileVersion,
  SharedArticleItem,
} from './types';

export const fileHubApi = {
  getSummary: async (role?: string): Promise<FileHubSummary> => {
    return apiRequest<FileHubSummary>('/file-hub/summary', {
      channel: 'platform',
      query: { role },
    });
  },

  listFiles: async (params?: {
    category?: string;
    search?: string;
    file_type?: string;
    status?: string;
    ai_only?: boolean;
    sort_by?: string;
    page?: number;
    page_size?: number;
    role?: string;
  }): Promise<FileListResponse> => {
    return apiRequest<FileListResponse>('/file-hub/files', {
      channel: 'platform',
      query: {
        category: params?.category === 'all' ? undefined : params?.category,
        search: params?.search,
        file_type: params?.file_type === 'all' ? undefined : params?.file_type,
        status: params?.status === 'all' ? undefined : params?.status,
        ai_only: params?.ai_only ? 'true' : undefined,
        sort_by: params?.sort_by,
        page: params?.page ? String(params.page) : undefined,
        page_size: params?.page_size ? String(params.page_size) : undefined,
        role: params?.role,
      },
    });
  },

  getFileDetail: async (file_id: string, role?: string): Promise<FileDetail> => {
    return apiRequest<FileDetail>(`/file-hub/files/${file_id}`, {
      channel: 'platform',
      query: { role },
    });
  },

  checkDuplicates: async (
    items: DuplicateCheckItemInput[]
  ): Promise<DuplicateCheckResultItem[]> => {
    const res = await apiRequest<{ results: DuplicateCheckResultItem[] }>(
      '/file-hub/check-duplicates',
      {
        method: 'POST',
        channel: 'platform',
        body: { items },
      }
    );
    return res.results;
  },

  uploadBatch: async (payload: BatchUploadPayload): Promise<BatchUploadResponse> => {
    return apiRequest<BatchUploadResponse>('/file-hub/upload-batch', {
      method: 'POST',
      channel: 'platform',
      body: payload,
    });
  },

  createVersion: async (
    file_id: string,
    payload: CreateVersionPayload
  ): Promise<FileVersion> => {
    return apiRequest<FileVersion>(`/file-hub/files/${file_id}/versions`, {
      method: 'POST',
      channel: 'platform',
      body: payload,
    });
  },

  restoreVersion: async (
    file_id: string,
    version_id: string
  ): Promise<FileVersion> => {
    return apiRequest<FileVersion>(
      `/file-hub/files/${file_id}/versions/${version_id}/restore`,
      {
        method: 'POST',
        channel: 'platform',
        body: { make_current: true },
      }
    );
  },

  archiveFile: async (
    file_id: string
  ): Promise<{ file_id: string; is_archived: boolean }> => {
    return apiRequest<{ file_id: string; is_archived: boolean }>(
      `/file-hub/files/${file_id}/archive`,
      {
        method: 'POST',
        channel: 'platform',
      }
    );
  },

  toggleAiSource: async (
    file_id: string,
    payload: { ai_enabled: boolean; priority?: number; citation_anchor?: string }
  ): Promise<FileAiSource> => {
    return apiRequest<FileAiSource>(`/file-hub/files/${file_id}/ai-toggle`, {
      method: 'POST',
      channel: 'platform',
      body: payload,
    });
  },

  updateAcl: async (
    file_id: string,
    acls: Array<{ scope_type: string; scope_id: string; permissions: string }>
  ): Promise<FileAcl[]> => {
    return apiRequest<FileAcl[]>(`/file-hub/files/${file_id}/acl`, {
      method: 'POST',
      channel: 'platform',
      body: { acls },
    });
  },

  addLink: async (
    file_id: string,
    payload: {
      entity_type: string;
      entity_id: string;
      entity_title: string;
      link_context?: string;
      version_id?: string;
    }
  ): Promise<FileLink> => {
    return apiRequest<FileLink>(`/file-hub/files/${file_id}/links`, {
      method: 'POST',
      channel: 'platform',
      body: payload,
    });
  },

  getSharedArticles: async (
    role?: string
  ): Promise<{ items: SharedArticleItem[]; total: number }> => {
    return apiRequest<{ items: SharedArticleItem[]; total: number }>(
      '/file-hub/shared-articles',
      {
        channel: 'platform',
        query: { role },
      }
    );
  },

  queryAi: async (
    query: string,
    role_context?: string
  ): Promise<AiQueryResponse> => {
    return apiRequest<AiQueryResponse>('/file-hub/ai-query', {
      method: 'POST',
      channel: 'platform',
      body: { query, role_context },
    });
  },

  listAudit: async (
    file_id?: string,
    action?: string,
    limit: number = 100
  ): Promise<{ items: FileAuditLog[]; total: number }> => {
    return apiRequest<{ items: FileAuditLog[]; total: number }>('/file-hub/audit', {
      channel: 'platform',
      query: { file_id, action, limit: String(limit) },
    });
  },

  downloadFile: async (
    file_id: string,
    version_id?: string,
    role?: string
  ): Promise<{
    file_id: string;
    original_name: string;
    mime_type: string;
    size_bytes: number;
    checksum: string;
    content_preview: string;
    storage_available?: boolean;
    version_id?: string;
    download_url: string;
  }> => {
    return apiRequest(`/file-hub/files/${file_id}/download`, {
      channel: 'platform',
      query: { version_id, role },
    });
  },

  previewFile: async (file_id: string, version_id?: string): Promise<FilePreview> => {
    return apiRequest(`/file-hub/files/${file_id}/preview`, {
      channel: 'platform',
      query: { version_id },
    });
  },

  downloadFileStream: async (file_id: string, version_id?: string): Promise<Blob> => {
    const { blob } = await apiRequestBlob(`/file-hub/files/${file_id}/download-stream`, {
      channel: 'platform',
      query: { version_id },
    });
    return blob;
  },
};
